import { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { useStoreState } from '@/stores/store';
import { useDebounce } from '@/hooks/useDebounce';
import { SetType, Template } from '@/components/Template/types';
import { fetchTemplates } from '@/api/services/templates';
import { logError } from '@/utils/logHandling';
import { SetEditorFlow, isTemplateFlow } from '@/contexts/SetEditorContext';
import { SET_DEFAULTS } from '@/contexts/SetEditorContext/constants';
import { useSetEditor } from '@/contexts/SetEditorContext/useSetEditor';
import { useSaveSet } from '@/hooks/useSaveSet';
import { useSetEditorCancel } from '@/hooks/useSetEditorCancel';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { Card } from '@/components/ui/therapist/Card';
import { Modal } from '@/components/ui/therapist/Modal';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { Pill } from '@/components/Pill';
import { IconName } from '@/components/NeolexonIcon/Icons/types';
import { Text } from '@/components/ui/therapist/Text';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/ui/therapist/Input';
import RadioButton from '@/components/ui/therapist/RadioButton';
import { Button } from '@/components/ui/therapist/Button';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { BodyWrapper } from '@/layouts/BodyWrapper';
import theme from '@/config/theme';
import { styles } from './styles';

const difficulties = [
  { value: 'easy', title: 'Leicht', icon: 'weightEasy' },
  { value: 'medium', title: 'Mittel', icon: 'weightMedium' },
  { value: 'hard', title: 'Schwer', icon: 'weightHard' },
] as const;

const SCREEN_TITLE: Record<SetEditorFlow, string> = {
  createSet: 'Set erstellen',
  editSet: 'Set bearbeiten',
  createTemplate: 'Vorlage erstellen',
  editTemplate: 'Vorlage bearbeiten',
  templateFromSet: 'Vorlage speichern',
  duplicateTemplate: 'Vorlage duplizieren',
};

type SetDetailsProps = StackScreenProps<TherapistStackParamList, 'SetDetails'>;

export function SetDetails({ navigation, route }: SetDetailsProps) {
  const { formData, setFormData, savePayload } = useSetEditor();
  const flow = route.params.flow;
  const { save } = useSaveSet(flow);
  const { isTherapistDemo } = useStoreState((store) => store);

  const isTemplateMode = isTemplateFlow(flow);
  // Only new templates are checked against existing names — includes duplicating
  // since the duplicate gets a new name and must also be unique.
  const isTemplateCreation =
    flow === 'createTemplate' ||
    flow === 'templateFromSet' ||
    flow === 'duplicateTemplate';

  const cancel = useSetEditorCancel(flow, savePayload?.patientId);

  const screenTitle = SCREEN_TITLE[flow];

  const defaults =
    SET_DEFAULTS[(savePayload?.apiKeyName as SetType) ?? 'wordsets'];

  const [templates, setTemplates] = useState<Template[]>([]);
  const [nameError, setNameError] = useState<string | undefined>();
  const [isDemoTherapistVisible, setIsDemoTherapistVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isTemplateCreation) {
      return;
    }
    const loadTemplates = async () => {
      try {
        const { templates: fetchedTemplates } = await fetchTemplates({
          setType: defaults.setType,
        });
        setTemplates(fetchedTemplates ?? []);
      } catch (error) {
        setTemplates([]);
        logError('Error fetching templates', error);
      }
    };
    loadTemplates();
  }, [isTemplateCreation, defaults.setType]);

  const isDuplicateName = useCallback(
    (name: string) =>
      templates.some(
        (template) =>
          template.name.trim().toLowerCase() === name.trim().toLowerCase(),
      ),
    [templates],
  );

  const { debouncedFunc: debouncedCheckName } = useDebounce((name: string) => {
    setNameError(
      name && isDuplicateName(name)
        ? 'Dieser Vorlagenname ist bereits vergeben.'
        : undefined,
    );
  }, 500);

  useEffect(() => {
    navigation.setOptions({
      title: screenTitle,
      headerTitleAllowFontScaling: false,
      headerLeft: () => (
        <AppHeaderAction
          primary
          onPress={() => navigation.goBack()}
          icon={<Icon name="chevronLeft" scale={1.2} />}
          text="Zurück"
        />
      ),
      headerRight: () => (
        <View style={{ gap: 30, flexDirection: 'row' }}>
          <AppHeaderAction
            primary
            onPress={cancel}
            icon={<Icon name="close" scale={1.2} />}
            text="Abbrechen"
          />
        </View>
      ),
    });
  }, [navigation, screenTitle, cancel]);

  const handleNameChange = useCallback(
    (value: string) => {
      setFormData({ name: value });
      if (isTemplateCreation) {
        setNameError(undefined);
        debouncedCheckName(value);
      }
    },
    [setFormData, isTemplateCreation, debouncedCheckName],
  );

  const handleAppDisplayNameChange = useCallback(
    (value: string) => {
      setFormData({ appDisplayName: value });
    },
    [setFormData],
  );

  const handleDifficultyChange = useCallback(
    (difficulty: 'easy' | 'medium' | 'hard') => {
      setFormData({ difficulty });
    },
    [setFormData],
  );

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setFormData({ description: value });
    },
    [setFormData],
  );

  const handleSave = useCallback(async () => {
    if (isSaving) {
      return;
    }
    if (isTherapistDemo) {
      setIsDemoTherapistVisible(true);
      return;
    }
    if (isTemplateCreation && isDuplicateName(formData.name)) {
      setNameError('Dieser Vorlagenname ist bereits vergeben.');
      return;
    }
    setIsSaving(true);
    try {
      await save();
    } finally {
      setIsSaving(false);
    }
  }, [
    isSaving,
    isTherapistDemo,
    isTemplateCreation,
    isDuplicateName,
    formData.name,
    save,
  ]);

  const isSaveDisabled =
    isSaving ||
    !savePayload ||
    (isTemplateMode &&
      (!formData.name || !formData.difficulty || !formData.description)) ||
    (isTemplateCreation && !!nameError);

  return (
    <BodyWrapper showSidebar={false} bodyStyle={{ width: '70%', flex: 1 }}>
      <KeyboardAwareScrollView
        enableOnAndroid
        extraScrollHeight={30}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <SafeAreaView style={styles.container}>
          <Modal
            isVisible={isDemoTherapistVisible}
            title="In der Demo nicht möglich"
            confirmActionButton={
              <Button
                label="OK"
                look="primary"
                onPress={() => setIsDemoTherapistVisible(false)}
              />
            }
            onClose={() => setIsDemoTherapistVisible(false)}
          >
            <Text style={{ fontSize: 18 }}>
              Registrieren Sie sich bei neolexon, um Ihre eigenen Patient:innen
              und Übungssets zu erstellen.
            </Text>
          </Modal>

          <Card>
            <Text style={styles.sectionTitle}>
              {isTemplateMode ? 'Vorlage speichern' : 'Set benennen'}
            </Text>

            {isTemplateMode ? (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                  Schwierigkeit
                  <Text style={styles.required}> *</Text>
                </Text>
                <Text style={styles.sectionDescription}>
                  Geben Sie an, wie anspruchsvoll die Vorlage ist.
                </Text>
                <View style={styles.typeSelection}>
                  {difficulties.map((difficulty) => {
                    const isSelected = formData.difficulty === difficulty.value;

                    return (
                      <PressableNeolexon
                        key={difficulty.value}
                        onPress={() => handleDifficultyChange(difficulty.value)}
                      >
                        <View style={styles.typeItem}>
                          <RadioButton
                            isSelected={isSelected}
                            onPress={() =>
                              handleDifficultyChange(difficulty.value)
                            }
                          />
                          <Pill
                            label={difficulty.title}
                            icon={difficulty.icon as IconName}
                            isNeolexon={false}
                            iconSize={22}
                          />
                        </View>
                      </PressableNeolexon>
                    );
                  })}
                </View>
              </View>
            ) : null}

            <View style={styles.section}>
              <Input
                testID="set-name-input"
                label={isTemplateMode ? 'Vorlage Name' : 'Set Name'}
                additionalInfo={`Ein eindeutiger Name, der Ihnen hilft, ${isTemplateMode ? 'die Vorlage' : 'das Set'} schnell und problemlos wiederzuerkennen.`}
                value={formData.name}
                style={{ maxWidth: '60%' }}
                onChange={handleNameChange}
                placeholder={isTemplateMode ? 'Vorlage Name' : defaults.name}
                required={isTemplateMode}
                status={nameError ? 'warning' : 'default'}
                statusMessage={nameError}
              />

              {!isTemplateMode && (
                <View style={{ marginTop: theme.spacings.lg }}>
                  <Input
                    label="Set Name für Eigentraining"
                    additionalInfo="Ein verständlicher Name, der in der Patienten-App angezeigt wird (maximal 16 Zeichen)."
                    value={formData.appDisplayName}
                    style={{ maxWidth: '60%' }}
                    onChange={handleAppDisplayNameChange}
                    maxLength={16}
                    placeholder={defaults.appDisplayName}
                  />
                </View>
              )}

              {isTemplateMode ? (
                <Input
                  label="Vorlage Beschreibung"
                  additionalInfo="Beschreiben Sie die Vorlage, um sie eindeutig erkennen zu können."
                  value={formData.description || ''}
                  style={{ maxWidth: '60%' }}
                  onChange={handleDescriptionChange}
                  placeholder="Beschreiben Sie die Vorlage"
                  required
                />
              ) : null}
            </View>
          </Card>

          <View style={styles.footer}>
            <Button
              label="Zurück"
              look="secondary"
              onPress={() => navigation.goBack()}
            />
            <Button
              testID="set-save-button"
              label="Speichern"
              disabled={isSaveDisabled}
              onPress={handleSave}
            />
          </View>
        </SafeAreaView>
      </KeyboardAwareScrollView>
    </BodyWrapper>
  );
}
